/** Libraries */
import Swal from "sweetalert2";

/** Utils */
import Toast from "@moltech/molui/toast";

/** Encryption */
import { decryptAES } from "../encryption/CryptoService";

const ensureAuthStatusCode = (apiData, meta) => {
    if (apiData?.statusCode || !isAuthenticationUrl(meta)) return;

    const authFlag = apiData?.isAuthenticated ?? apiData?.responseItem?.responseContent?.isAuthenticated;
    if (authFlag !== undefined) {
        apiData.statusCode = authFlag ? 200 : 401;
    }
};

const isAuthenticationUrl = (meta) => meta.request.url.includes("Authentication");

const isSuccessResponse = (apiData) => {
    return apiData === true || apiData?.statusCode === 200 || Array.isArray(apiData) || apiData instanceof Blob || (!!apiData && typeof apiData === 'object');
};

const handleAuthFailure = (apiData) => {
    const isAuth = apiData?.isAuthenticated ?? apiData?.responseItem?.responseContent?.isAuthenticated;
    const message = apiData?.message ?? apiData?.responseItem?.responseContent?.message;
    if (isAuth === false && message) {
        Toast.error(message);
        return false;
    }
    return true;
};

const extractResponseData = (apiData) => {
    const wrapped = apiData?.responseData ?? apiData?.ResponseData;
    if (wrapped?.data != null || wrapped?.Data != null) {
        return {
            data: wrapped.data ?? wrapped.Data,
            message: wrapped.message ?? wrapped.Message,
            statusCode: wrapped.statusCode ?? wrapped.StatusCode,
            success: wrapped.success ?? wrapped.Success,
        };
    }
    if (Array.isArray(wrapped)) {
        return wrapped;
    }
    if (wrapped && typeof wrapped === 'object') {
        // Normalize PascalCase ApiResponse fields from encryption-off wrapper.
        if (
            (wrapped.success != null || wrapped.Success != null) &&
            (wrapped.data != null || wrapped.Data != null)
        ) {
            return {
                data: wrapped.data ?? wrapped.Data,
                message: wrapped.message ?? wrapped.Message,
                statusCode: wrapped.statusCode ?? wrapped.StatusCode,
                success: wrapped.success ?? wrapped.Success,
            };
        }
        return wrapped;
    }
    return apiData;
};

export const transformSuccessResponse = async (response, meta) => {
    let resData = decryptAndParse(response);

    ensureAuthStatusCode(resData, meta);

    if (!isSuccessResponse(resData)) {
        Toast.error(resData.message || "Something went wrong with API. Please contact admin!!");
        return null;
    }

    if (isAuthenticationUrl(meta)) {
        const authCheck = handleAuthFailure(resData);
        if (!authCheck) return null;
    }

    const data = extractResponseData(resData);
    return data;
};

const normalizeError = (response) => {
    return {
        type: response?.status,
        statusCode:
            response?.data?.responseData?.statusCode ??
            response?.data?.statusCode ??
            response?.originalStatus ??
            (typeof response?.status === "number" ? response.status : null),

        data: response.data,
        raw: response
    };
};

const decryptAndParse = (data) => {
    if (!data) return data;
    if (data.isEnType) {
        const decrypted = decryptAES(data);
        return decrypted ? JSON.parse(decrypted) : data;
    }
    if (data.responseData) {
        return data.responseData;
    }
    return data;
};

const handleValidationError = (resData) => {

    if (!resData?.errors) return;

    const shortenErrorMessage = (message) => {
        if (!message) return message;

        return message.split(" Path:")[0];
    };

    const errorListHtml = Object.entries(resData.errors)
        .flatMap(([field, messages]) => {
            const cleanField = field.replace(/^\$\.?/, ""); // clean up $. or $
            return messages.map(msg => {
                const cleanMsg = shortenErrorMessage(msg);
                return `
                    <div class="swal-validation-error-item">
                        <div class="swal-validation-error-dot"></div>
                        <div class="swal-validation-error-content">
                            ${cleanField ? `<span class="swal-validation-error-field">${cleanField}</span>` : ""}
                            <span class="swal-validation-error-msg">${cleanMsg}</span>
                        </div>
                    </div>
                `;
            });
        })
        .join("");

    Swal.fire({
        title: "Validation Error",
        html: `
            <div class="swal-validation-errors-wrapper">
                <p class="swal-validation-errors-subtitle">Please resolve the following issues to continue:</p>
                <div class="swal-validation-errors-list">
                    ${errorListHtml}
                </div>
            </div>
        `,
        icon: "error",
        customClass: {
            popup: "swal-premium-popup swal-validation-popup",
            title: "swal-premium-title",
            htmlContainer: "swal-premium-html-container",
            confirmButton: "swal-premium-confirm-btn"
        },
        buttonsStyling: false,
        confirmButtonText: "OK"
    });
};


const isQuietOptionalUrl = (meta) => {
    const url = String(meta?.request?.url || meta?.arg?.url || "");
    return url.includes("MolAssistThreads");
};

const handleHttpErrors = (statusCode, resData, meta) => {
    const quiet = isQuietOptionalUrl(meta);
    switch (statusCode) {
        case 400:
            if (resData?.errors) handleValidationError(resData);
            return resData;

        case 401:
            // Redirect is handled in fetchBaseQuery; avoid duplicate toast spam.
            if (!quiet) Toast.error(resData?.message || "Session expired. Please login again.");
            return null;

        case 500:
            if (!quiet) Toast.error("Something went wrong with the API call! Please contact admin.");
            return null;

        default:
            return resData;
    }
};

export const transformErrorResponse = (response, meta) => {
    const error = normalizeError(response);
    const quiet = isQuietOptionalUrl(meta);

    if (error.type === "FETCH_ERROR") {
        // Authenticated API down / closed: fetchBaseQuery redirects to login.
        // Keep toast quiet when redirect is already in progress from the same failure.
        if (!quiet) Toast.error("Unable to reach the server. Please login again.");
        return null;
    };

    if (error.type === "PARSING_ERROR" && error.statusCode === 500) {
        if (!quiet) {
            const msg = response.data?.toLowerCase?.() || "";
            const isDbError = ["sql", "connection", "provider"].some(k => msg.includes(k));

            Toast.error(
                isDbError
                    ? "Database connection issue. Please try again later."
                    : "Invalid server response. Please try again later."
            );
        }
        return null;
    };

    let apiData = decryptAndParse(error.data);

    if (apiData?.responseData) {
        apiData = apiData.responseData;
    }

    ensureAuthStatusCode(apiData, meta);

    if (error.statusCode && error.statusCode !== 200) {
        return handleHttpErrors(error.statusCode, apiData, meta);
    }

    return null;

}
